import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './DamageDice.css'
import {WAR_COLOR} from "../../../styles/constants";
import {showToast} from "../../../redux/actions";
import {MINUS_SIGN} from "../../../constants/text";
import {DAMAGE_DICE_MAX_VALUE} from "../../../configs/damage";
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {
  TEXT_ALREADY_REROLLED,
  TEXT_DICE_ALREADY_BEST,
  TEXT_TARGET_UNIT_ALREADY_KILLED,
  TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT,
  TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY,
  TEXT_YOU_CANNOT_REROLL_ENEMY_DICES
} from "../../../constants/text/combat";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {usePrev} from "../../../hooks/react/usePrev";
import {REROLL_ANIMATION_DELAY_MS, REROLL_ANIMATION_DURATION_MS} from "../../../constants/visuals";
import {selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {checkIfCurrentUserOwner} from "../../../utils/gameState/user/checkIfCurrentUserOwner";
import {
  getDamageDiceFaceLayout,
  getDamageDiceFacePosition,
  getDamageDiceOrientation,
} from "../../../utils/dice/damageDiceOrientation";

// Базовый наклон покоя — кость «смотрит» в верхний левый угол, чтобы объем
// был виден сразу, а не только при движении мыши (и на тач-устройствах, где
// mousemove не приходит вовсе). Значения продублированы в DamageDice.css
// (.damage-dice-tilt) — CSS дает ту же позу до первого события мыши
const BASE_TILT_X_DEG = 9;
const BASE_TILT_Y_DEG = -11;

// Максимальный доворот куба к курсору и расстояние (px), на котором эффект
// насыщается: вблизи кость охотно «смотрит» на курсор, издалека еле заметно
const CURSOR_TILT_MAX_DEG = 14;
const CURSOR_TILT_SATURATION_PX = 280;

// Кувырок переброса: сколько полных оборотов делает кость по каждой оси.
// Финальная ориентация от лишних оборотов не меняется (кратны 360), поэтому
// кость гарантированно останавливается новой выпавшей гранью к зрителю
const ROLL_MIN_FULL_TURNS = 2;
const ROLL_EXTRA_FULL_TURNS = 2;

const formatFaceValue = (value) => {
  if (value > 0) return `+${value}`;
  if (value < 0) return `${MINUS_SIGN}${Math.abs(value)}`;
  return '0';
};

/**
 * Кость урона — псевдо-3D кубик на CSS с настоящими шестью гранями
 * (-3, -2, -1, -1, 0, 0 — как у getDamageDiceRoll), повернутый выпавшей
 * гранью к зрителю.
 *
 * Живость: кубик слегка доворачивается в сторону курсора (глобальный
 * mousemove, запись прямо в style через rAF — без ререндеров). Выбор для
 * переброса подсвечивает грани красным свечением; сам переброс — кувырок с
 * подлетом на несколько полных оборотов по трем осям, приземляющийся новой
 * гранью (CSS transition до вычисленной ориентации, поэтому «недокрутить»
 * кость не может).
 */
const DamageDice = (
  {
    ownerId,
    hasAdditionalSupply,
    hasRerolled,
    isTargetAlreadyKilled,
    isAlreadyBestRollResult,
    damage,
    isAccounted,
    // Кость брошена, но в урон не идет (у селестиалов из 4 костей учитываются
    // 2 лучшие): приглушена, перебросить ее можно
    isDiscarded,
    isSelected,
    setSelected,
    isShootingDice,
    isUnitMeleeElf,
    shouldBounce,
    // Витрина (обучающие модалки, правила): кость только показывают, кликать
    // и перебрасывать ее нельзя
    isPresentation,
    // Плавный поворот к новому значению вместо мгновенного (мс). Без кувырка
    // переброса: это не бросок, а «живая» иллюстрация
    turnDurationMs,
  }
) => {
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);

  const [isRolling, setIsRolling] = useState(false);
  // Значение, которым кость повернута к зрителю. Отдельное от пропса damage
  // намеренно: в кадре переброса кость обязана продолжать показывать СТАРУЮ
  // грань — новая ориентация уезжает в DOM только следующим кадром (rAF), уже
  // с вооруженным transition. Если бы новая ориентация попала в DOM сразу
  // (даже на один пересчет стилей до включения перехода — например, из-за
  // чтения геометрии в чужом layout-эффекте), браузер зафиксировал бы ЕЕ как
  // стартовую позу, и глаз успевал замечать новое значение до кувырка
  const [displayedDamage, setDisplayedDamage] = useState(damage);
  // Накопленные полные обороты кувырков: остаются в transform навсегда
  // (кратны 360 и на позу не влияют), зато переход к следующему броску
  // всегда идет «вперед», а не раскручивается обратно.
  // Это состояние, а НЕ ref: если переброс выпал тем же значением,
  // setDisplayedDamage схлопывается в no-op и рендер не происходит — обороты
  // из ref не доехали бы до DOM, и кость подпрыгивала бы не вращаясь.
  // Новый объект оборотов запускает рендер всегда
  const [spins, setSpins] = useState({x: 0, y: 0, z: 0});
  const delayRef = useRef(0);
  const tiltRef = useRef(null);
  const landingTimeoutRef = useRef(null);
  const rollStartRafRef = useRef(null);

  const prevIsSelected = usePrev(isSelected);
  const prevHasRerolled = usePrev(hasRerolled);

  // Именно useLayoutEffect, не useEffect: обычный эффект срабатывает ПОСЛЕ
  // отрисовки кадра, и первый кадр после переброса успевал показать куб уже
  // повернутым новой гранью без анимации — глаз замечал новое значение до
  // старта кувырка. Layout-эффект выполняется до отрисовки: setIsRolling
  // синхронно дорисовывает transition, и браузер стартует анимацию из СТАРОЙ
  // зафиксированной позы, не показав промежуточную ни на кадр
  useLayoutEffect(() => {
    // Кувыркаются только кости, которые игрок выбирал на переброс: у остальных
    // hasRerolled тоже поднимется, но выбранными они не были
    const isRerollHappened = hasRerolled && !prevHasRerolled
      && !isSelected && prevIsSelected;
    if (!isRerollHappened) {
      // Обычная смена значения (новый расчет боя) — мгновенный поворот. Но не
      // между стартом переброса и его rAF-кадром: ранняя синхронизация
      // раскрыла бы новую грань до включения перехода
      if (!isRolling && rollStartRafRef.current === null) {
        setDisplayedDamage(damage);
      }
      return;
    }

    delayRef.current = Math.floor(Math.random() * REROLL_ANIMATION_DELAY_MS);
    setIsRolling(true);

    // Двухфазный старт кувырка. Фаза 1 (этот коммит): кость все еще повернута
    // СТАРОЙ гранью, но transition уже вооружен. Фаза 2 (следующий кадр):
    // добавляем обороты и поворачиваем к новому значению — переход стартует
    // из зафиксированной старой позы. Новая грань не существует в DOM ни
    // одного пересчета стилей без полных оборотов перед ней, поэтому
    // подсмотреть ее до анимации невозможно ни при каком порядке отрисовки
    const randomTurns = () => 360 * (ROLL_MIN_FULL_TURNS + Math.floor(Math.random() * (ROLL_EXTRA_FULL_TURNS + 1)));
    const randomDirection = () => (Math.random() < 0.5 ? -1 : 1);
    if (rollStartRafRef.current) {
      cancelAnimationFrame(rollStartRafRef.current);
    }
    rollStartRafRef.current = requestAnimationFrame(() => {
      rollStartRafRef.current = null;
      setSpins((prevSpins) => ({
        x: prevSpins.x + randomTurns(),
        y: prevSpins.y + randomDirection() * randomTurns(),
        z: prevSpins.z + randomDirection() * 360,
      }));
      setDisplayedDamage(damage);
    });

    // Небольшой запас поверх transition: ранний сброс отключил бы переход
    // до приземления и кость щелкнула бы в конечную позу. Таймер в ref, а НЕ
    // в cleanup эффекта: usePrev меняет зависимости на следующем же рендере,
    // и cleanup погасил бы таймер сразу после старта — кость зависла бы в
    // «полете» навсегда (та же ловушка, что и в старой реализации)
    if (landingTimeoutRef.current) {
      clearTimeout(landingTimeoutRef.current);
    }
    landingTimeoutRef.current = setTimeout(() => {
      setIsRolling(false);
      landingTimeoutRef.current = null;
    }, REROLL_ANIMATION_DURATION_MS + delayRef.current + 80);
  }, [isSelected, hasRerolled, prevIsSelected, prevHasRerolled, damage, isRolling]);

  // Очистка таймера и отложенного старта — только при размонтировании
  useEffect(() => () => {
    if (landingTimeoutRef.current) {
      clearTimeout(landingTimeoutRef.current);
    }
    if (rollStartRafRef.current) {
      cancelAnimationFrame(rollStartRafRef.current);
    }
  }, []);

  // Доворот к курсору/пальцу: пишем transform напрямую, минуя React, —
  // mousemove/touchmove слишком частые для setState, а перерисовывать формулу
  // урона незачем.
  //
  // Тач: кость следит за пальцем, только если жест НАЧАЛСЯ внутри футера
  // (.app-footer). Палец на игровом поле панорамирует камеру — следящая за
  // ним кость дергалась бы без всякого смысла. Отдельное слежение за touch
  // необходимо: браузер эмулирует mousemove только по тапу (отсюда прежний
  // мгновенный доворот к точке касания), непрерывное ведение пальцем шлет
  // touchmove, которого mousemove-слушатель не видит
  useEffect(() => {
    let rafId = null;
    let lastPoint = null;
    let trackedTouchId = null;

    const scheduleTilt = (clientX, clientY) => {
      lastPoint = {clientX, clientY};
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const element = tiltRef.current;
        if (!element || !lastPoint) {
          return;
        }
        const rect = element.getBoundingClientRect();
        const dx = lastPoint.clientX - (rect.left + rect.width / 2);
        const dy = lastPoint.clientY - (rect.top + rect.height / 2);
        // Доворот складывается с базовым наклоном покоя — кость и следит за
        // указателем, и не теряет объемную позу
        const tiltY = BASE_TILT_Y_DEG + CURSOR_TILT_MAX_DEG * Math.tanh(dx / CURSOR_TILT_SATURATION_PX);
        const tiltX = BASE_TILT_X_DEG - CURSOR_TILT_MAX_DEG * Math.tanh(dy / CURSOR_TILT_SATURATION_PX);
        element.style.transform = `rotateX(${tiltX.toFixed(2)}deg) rotateY(${tiltY.toFixed(2)}deg)`;
      });
    };

    const handleMouseMove = (event) => scheduleTilt(event.clientX, event.clientY);

    const handleTouchStart = (event) => {
      if (trackedTouchId !== null) {
        return;
      }
      // Футер ищется в момент касания, а не на монтировании: кость могла
      // перемонтироваться, а сам футер — пересоздаться
      const footer = tiltRef.current ? tiltRef.current.closest('.app-footer') : null;
      if (!footer) {
        return;
      }
      const startedInFooter = Array.from(event.changedTouches)
        .find((touch) => touch.target instanceof Node && footer.contains(touch.target));
      if (startedInFooter) {
        trackedTouchId = startedInFooter.identifier;
        scheduleTilt(startedInFooter.clientX, startedInFooter.clientY);
      }
    };

    const handleTouchMove = (event) => {
      if (trackedTouchId === null) {
        return;
      }
      const touch = Array.from(event.touches).find((item) => item.identifier === trackedTouchId);
      if (touch) {
        scheduleTilt(touch.clientX, touch.clientY);
      }
    };

    const handleTouchEnd = (event) => {
      if (trackedTouchId === null) {
        return;
      }
      // Палец отпущен (или касание отменено системным жестом) — слежение
      // заканчивается, кость остается в последней позе, как и после мыши
      const ended = Array.from(event.changedTouches)
        .some((touch) => touch.identifier === trackedTouchId);
      if (ended) {
        trackedTouchId = null;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart, {passive: true});
    window.addEventListener('touchmove', handleTouchMove, {passive: true});
    window.addEventListener('touchend', handleTouchEnd, {passive: true});
    window.addEventListener('touchcancel', handleTouchEnd, {passive: true});
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Не меньше лучшей грани: в записях старых партий встречается прежняя
  // лучшая грань +1 — она тоже критический успех
  const isMaxResult = damage >= DAMAGE_DICE_MAX_VALUE

  const onClick = () => {
    if (isPresentation) {
      return;
    }
    if (!checkIfCurrentUserOwner(userId, ownerId)) {
      dispatch(showToast(TEXT_YOU_CANNOT_REROLL_ENEMY_DICES, 'warning', 3000));
      return;
    }

    if (isMaxResult) {
      dispatch(showToast(TEXT_DICE_ALREADY_BEST, 'success', 3000));
      return;
    }
    if (hasRerolled) {
      dispatch(showToast(TEXT_ALREADY_REROLLED, 'warning', 3000));
      return;
    }
    if (!hasAdditionalSupply) {
      dispatch(showToast(TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY, 'warning', 3000));
      return;
    }
    if (isTargetAlreadyKilled) {
      dispatch(showToast(TEXT_TARGET_UNIT_ALREADY_KILLED, 'success', 3000))
      return;
    }
    if (isAlreadyBestRollResult) {
      dispatch(showToast(TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT, 'success', 3000))
      return;
    }

    const soundManger = REGISTRY_SINGLETON.getSoundManager();
    if (isSelected) {
      soundManger.playUndoClick();
    } else {
      if (isShootingDice && !isUnitMeleeElf) {
        soundManger.playShootingD4DiceSelect();
      } else {
        soundManger.playMeleeDiceSelect();
      }
    }

    setSelected(!isSelected)
  }

  const orientation = getDamageDiceOrientation(displayedDamage);
  // Свечение выбора — только на грани, повернутой к зрителю: на гранях,
  // стоящих ребром к экрану, box-shadow вырождается в плоскую тень-артефакт
  const litFacePosition = isSelected ? getDamageDiceFacePosition(displayedDamage) : null;
  const cubeStyle = {
    transform: `rotateX(${orientation.x + spins.x}deg)`
      + ` rotateY(${orientation.y + spins.y}deg)`
      + ` rotateZ(${spins.z}deg)`,
    // Переход включается только на время кувырка: обычная смена значения
    // (новый расчет боя) поворачивает кость мгновенно. Исключение —
    // turnDurationMs витрины: там смена значения и есть весь эффект
    transition: isRolling
      ? `transform ${REROLL_ANIMATION_DURATION_MS}ms cubic-bezier(0.16, 0.84, 0.3, 1.04) ${delayRef.current}ms`
      : (turnDurationMs ? `transform ${turnDurationMs}ms cubic-bezier(0.34, 0.9, 0.3, 1)` : 'none'),
  };

  return (
    <div
      className={`damage-dice ${
        addClassNameIfTrue(isPresentation, 'damage-dice_presentation')} ${
        addClassNameIfTrue(isAccounted, 'damage-dice-accounted')} ${
        addClassNameIfTrue(isDiscarded && !isSelected, 'damage-dice-discarded')} ${
        addClassNameIfTrue(shouldBounce && !isMaxResult, 'damage-dice-bounce')} ${
        addClassNameIfTrue(isSelected, 'damage-dice-selected')}`
      }
      onClick={onClick}
      title={`Кость урона\nГрани от ${MINUS_SIGN}3 до 0\n(${MINUS_SIGN}3, ${MINUS_SIGN}2, ${MINUS_SIGN}1, ${MINUS_SIGN}1, 0, 0)\nСреднее значение ≈ ${MINUS_SIGN}1,17`}
    >
      <div
        className={`damage-dice-scene ${addClassNameIfTrue(isRolling, 'damage-dice-scene_rolling')}`}
        style={isRolling ? {
          animationDuration: `${REROLL_ANIMATION_DURATION_MS}ms`,
          animationDelay: `${delayRef.current}ms`,
        } : undefined}
      >
        <div className={'damage-dice-tilt'} ref={tiltRef}>
          <div className={'damage-dice-cube'} style={cubeStyle}>
            {getDamageDiceFaceLayout(displayedDamage).map(({position, value}) => (
              <div
                key={position}
                className={`damage-dice-face damage-dice-face_${position} ${
                  addClassNameIfTrue(position === litFacePosition, 'damage-dice-face_lit')}`}
                style={value >= DAMAGE_DICE_MAX_VALUE ? {color: WAR_COLOR} : undefined}
              >
                {formatFaceValue(value)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DamageDice;
